import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Plus, Trash2, AlertTriangle } from "lucide-react";

/**
 * "Record Customer PO" — the customer sends a PO; we RECORD it.
 * The PO Number is a MANDATORY MANUAL entry (the customer's own PO number).
 * Partial-line selection is supported: unchecked quotation lines are marked
 * "Cancelled/Not Ordered" on the quotation server-side.
 */
export default function PoForm() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const quotation_id = params.get("quotation_id");
  const [quotation, setQuotation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!quotation_id);

  // Selected/editable quotation lines (with partial-selection metadata)
  const [poLines, setPoLines] = useState([]);

  const [form, setForm] = useState({
    quotation_id: quotation_id || "",
    po_number: "",
    po_date: new Date().toISOString().slice(0, 10),
    delivery_schedule: [{ date: "", quantity: 1, notes: "" }],
    remarks: "",
  });

  useEffect(() => {
    if (!quotation_id) return;
    api.get(`/quotations/${quotation_id}`).then((r) => {
      const q = r.data;
      setQuotation(q);
      // Seed with every quotation line selected (default full order); user can uncheck for partial PO
      setPoLines(
        (q.line_items || []).map((li) => ({
          source_quotation_line_id: li.id,
          selected: true,
          description: li.description,
          cylinder_type: li.cylinder_type,
          manufacturing_type: li.manufacturing_type || "New",
          customer_material_code: li.customer_material_code || null,
          application: li.application || null,
          bore_mm: li.bore_mm,
          rod_diameter_mm: li.rod_diameter_mm,
          stroke_mm: li.stroke_mm,
          quantity: li.quantity,               // editable — user may reduce for partial PO
          max_quantity: li.quantity,           // upper bound (informational)
          operating_pressure_bar: li.operating_pressure_bar,
          remarks: li.remarks || null,
          unit_price: li.unit_price,
        }))
      );
    }).finally(() => setLoading(false));
  }, [quotation_id]);

  function toggleLine(idx) {
    setPoLines(poLines.map((l, i) => i === idx ? { ...l, selected: !l.selected } : l));
  }
  function updateLineQty(idx, qty) {
    const q = Math.max(1, parseInt(qty || 0) || 1);
    setPoLines(poLines.map((l, i) => i === idx ? { ...l, quantity: q } : l));
  }
  function updateLinePrice(idx, price) {
    const p = parseFloat(price || 0) || 0;
    setPoLines(poLines.map((l, i) => i === idx ? { ...l, unit_price: p } : l));
  }
  function updateSchedule(i, patch) {
    setForm({ ...form, delivery_schedule: form.delivery_schedule.map((s, idx) => idx === i ? { ...s, ...patch } : s) });
  }
  function addSchedule() {
    setForm({ ...form, delivery_schedule: [...form.delivery_schedule, { date: "", quantity: 1, notes: "" }] });
  }
  function removeSchedule(i) {
    setForm({ ...form, delivery_schedule: form.delivery_schedule.filter((_, idx) => idx !== i) });
  }

  const selectedLines = poLines.filter((l) => l.selected);
  const unselectedCount = poLines.length - selectedLines.length;
  const totalPoValue = selectedLines.reduce((s, l) => s + (l.unit_price || 0) * (l.quantity || 0), 0);

  async function submit(e) {
    e.preventDefault();
    if (!form.quotation_id) { toast.error("Quotation link required"); return; }
    if (!form.po_number.trim()) { toast.error("Customer PO Number is required (enter it exactly as received)"); return; }
    if (selectedLines.length === 0) { toast.error("Select at least one line item to record on this PO"); return; }
    setSaving(true);
    try {
      const payload = {
        quotation_id: form.quotation_id,
        po_number: form.po_number.trim(),
        po_date: form.po_date,
        delivery_schedule: form.delivery_schedule.filter((d) => d.date && d.quantity > 0),
        remarks: form.remarks,
        line_items: selectedLines.map((l) => {
          const { selected, max_quantity, ...clean } = l;   // eslint-disable-line no-unused-vars
          return clean;
        }),
      };
      const { data } = await api.post("/purchase-orders", payload);
      toast.success(`Customer PO ${data.po_number} recorded`);
      nav(`/purchase-orders/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  }

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-900" /></div>;

  return (
    <div data-testid="po-form-page" className="max-w-5xl space-y-5">
      <div>
        <button onClick={() => nav(-1)} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-2">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">Commercial Workflow · Stage 3</div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Record Customer PO</h1>
        <p className="text-xs text-slate-500 mt-1">
          The Customer PO is an external document. Enter the number exactly as received —
          HISPL never generates PO numbers.
        </p>
        {quotation && (
          <p className="text-sm text-slate-600 mt-2">
            Against quotation <span className="font-mono font-semibold">{quotation.display_number || quotation.quotation_number}</span> · {quotation.customer_name}
          </p>
        )}
      </div>

      <form onSubmit={submit} className="space-y-5">
        <Card className="border-slate-200 p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Customer PO Number *</Label>
            <Input
              data-testid="po-number"
              value={form.po_number}
              onChange={(e) => setForm({ ...form, po_number: e.target.value })}
              className="mt-1 font-mono"
              placeholder="e.g. PO-4589 / 4500012789 / JSW/PO/245"
              required
            />
            <p className="mt-1 text-[10px] text-slate-500">Manual entry. Any format the customer uses is accepted (no uniqueness check).</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Customer PO Date *</Label>
            <Input
              type="date"
              data-testid="po-date"
              value={form.po_date}
              onChange={(e) => setForm({ ...form, po_date: e.target.value })}
              className="mt-1"
              required
            />
          </div>
        </Card>

        {/* Line-item selector — supports partial conversion */}
        <Card className="border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>
                Line Items on this Customer PO
              </div>
              <div className="text-xs text-slate-500">
                Uncheck lines the customer did NOT order — they will be marked
                <span className="ml-1 px-1.5 py-0.5 bg-red-50 border border-red-200 text-red-700 rounded font-mono text-[10px]">Cancelled/Not Ordered</span>
                on the source quotation.
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">PO Value (₹)</div>
              <div className="font-mono text-lg font-bold text-slate-900" data-testid="po-total-value">
                ₹{totalPoValue.toLocaleString("en-IN")}
              </div>
            </div>
          </div>
          {unselectedCount > 0 && (
            <div className="mx-5 mt-3 text-[11px] flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-900 rounded px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span data-testid="partial-conversion-warning">
                Partial PO — {unselectedCount} of {poLines.length} quotation line{poLines.length > 1 ? "s" : ""} will be marked <strong>Cancelled/Not Ordered</strong>.
              </span>
            </div>
          )}
          <div className="p-5 space-y-3">
            {poLines.length === 0 && (
              <div className="text-xs text-slate-400 italic">No line items on the source quotation.</div>
            )}
            {poLines.map((l, i) => (
              <div key={l.source_quotation_line_id || i}
                   data-testid={`po-line-${i}`}
                   className={`p-3 rounded-lg border transition-colors ${l.selected ? "border-blue-300 bg-blue-50/30" : "border-slate-200 bg-slate-50 opacity-70"}`}>
                <div className="flex items-start gap-3">
                  <Checkbox
                    data-testid={`po-line-check-${i}`}
                    checked={l.selected}
                    onCheckedChange={() => toggleLine(i)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{l.description}</div>
                    <div className="text-[11px] text-slate-500 font-mono">
                      Bore {l.bore_mm} · Rod Ø {l.rod_diameter_mm} · Stroke {l.stroke_mm} mm
                      {l.operating_pressure_bar ? ` · ${l.operating_pressure_bar} bar` : ""}
                      {l.customer_material_code ? ` · MC: ${l.customer_material_code}` : ""}
                    </div>
                  </div>
                  <div className="flex items-end gap-3">
                    <div className="w-24">
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                        Qty <span className="text-slate-400">(max {l.max_quantity})</span>
                      </Label>
                      <Input
                        type="number"
                        min={1}
                        max={l.max_quantity}
                        disabled={!l.selected}
                        value={l.quantity}
                        onChange={(e) => updateLineQty(i, e.target.value)}
                        data-testid={`po-line-qty-${i}`}
                        className="mt-1 h-8 font-mono"
                      />
                    </div>
                    <div className="w-32">
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">Unit Price ₹</Label>
                      <Input
                        type="number"
                        step="0.01"
                        disabled={!l.selected}
                        value={l.unit_price ?? ""}
                        onChange={(e) => updateLinePrice(i, e.target.value)}
                        data-testid={`po-line-price-${i}`}
                        className="mt-1 h-8 font-mono"
                      />
                    </div>
                    <div className="w-28 text-right">
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">Line Total</Label>
                      <div className="mt-1 h-8 flex items-center justify-end font-mono text-sm font-semibold text-slate-900">
                        ₹{((l.unit_price || 0) * (l.quantity || 0)).toLocaleString("en-IN")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Delivery Schedule</div>
              <div className="text-xs text-slate-500">Split delivery into multiple dates if the PO specifies staggered deliveries.</div>
            </div>
            <Button type="button" size="sm" variant="outline" onClick={addSchedule} data-testid="add-schedule">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Line
            </Button>
          </div>
          <div className="p-5 space-y-2">
            {form.delivery_schedule.map((s, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`schedule-${i}`}>
                <div className="col-span-4">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-600">Date</Label>
                  <Input type="date" value={s.date}
                         onChange={(e) => updateSchedule(i, { date: e.target.value })} className="mt-1 h-8" />
                </div>
                <div className="col-span-3">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-600">Qty</Label>
                  <Input type="number" min="1" value={s.quantity}
                         onChange={(e) => updateSchedule(i, { quantity: parseInt(e.target.value) })}
                         className="mt-1 h-8 font-mono" />
                </div>
                <div className="col-span-4">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-600">Notes</Label>
                  <Input value={s.notes || ""} onChange={(e) => updateSchedule(i, { notes: e.target.value })}
                         className="mt-1 h-8" placeholder="Batch, phase, etc." />
                </div>
                <div className="col-span-1">
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeSchedule(i)}
                          className="h-8 w-8 text-red-600">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="border-slate-200 p-5">
          <Label className="text-xs uppercase tracking-wider text-slate-600">Remarks</Label>
          <Textarea value={form.remarks} rows={2}
                    onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="mt-1" />
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => nav(-1)}>Cancel</Button>
          <Button type="submit" data-testid="po-submit" disabled={saving} className="bg-blue-900 hover:bg-blue-800 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Record Customer PO"}
          </Button>
        </div>
      </form>
    </div>
  );
}
